import { Component } from '@xeokit/xeokit-sdk';
import type { MetadataPropertySet } from '~/stores/use-model-databases';

const IFC_PROGRESS_LABELS: Record<string, string> = {
  read: 'Reading',
  parse: 'Parsing',
  geom: 'Creating geometry',
  pack: 'Packing geometry',
  edges: 'Creating edges',
  scene: 'Building scene',
};

// "read" counts megabytes, with total 0 when the size is unknown
function describeIfcProgress(phase: string, done: number, total: number) {
  const label = IFC_PROGRESS_LABELS[phase] ?? 'Loading';
  if (total > 0) return `${label}... ${Math.round((done / total) * 100)}%`;
  return phase === 'read' ? `${label}... ${done} MB` : `${label}...`;
}

export async function loadModelIFC(
  id: string,
  source: string,
  options?: {
    edges?: boolean;
    position?: (number | null | undefined)[];
  },
): Promise<string | undefined> {
  const viewerStore = useViewerStore();
  const processesStore = useProcessesStore();
  const loader = viewerStore.getLoaders().xeoIfcLoaderPlugin;

  if (!viewerStore.isInitialized || !loader) return;

  const t0 = performance.now();

  // The plugin outputs Y-up like the XKT and glTF loaders, so rotate into the Z-up camera world axis too
  const sceneModel = loader.load({
    id,
    src: source,
    edges: options?.edges ?? false,
    rotation: [90, 0, 0],
    onProgress: (phase, done, total) => {
      processesStore.message = describeIfcProgress(phase, done, total);
    },
  });

  return new Promise((resolve, reject) => {
    sceneModel.on('loaded', function () {
      processesStore.message = '';
      console.log(`Model ${id} loaded in ${Math.floor(performance.now() - t0) / 1000.0} seconds`);

      if (options) {
        updateModel(sceneModel, {
          position: options.position,
        });
      }

      viewerStore.saveModelMemento(sceneModel.id);
      resolve(String(sceneModel.id));
    });

    sceneModel.on('error', function (error) {
      processesStore.message = '';
      console.error(error);
      reject(error);
    });

    // Losing the WebGL context destroys the viewer mid-load, so 'loaded' would never fire
    (sceneModel as Component).on('destroyed', function () {
      processesStore.message = '';
      reject(new Error(`Model ${id} was destroyed before it finished loading`));
    });
  });
}

// The plugin keeps property sets in its worker instead of the metamodel; empty for objects of other loaders
export async function getIfcPropertySets(objectId: string): Promise<MetadataPropertySet[]> {
  const loader = useViewerStore().getLoaders().xeoIfcLoaderPlugin;
  if (!loader) return [];

  try {
    const propertySets = await loader.getProperties(objectId);
    let i = 0;

    return propertySets.map((propertySet, index) => ({
      id: String(index),
      name: propertySet.pset,
      type: '',
      properties: propertySet.props.map((property) => {
        i++;

        return {
          id: String(i),
          name: property.name,
          ifcPropertyType: '',
          ifcValueType: '',
          value: property.value,
          valueType: '',
          unitId: null,
          unitName: property.unit ?? null,
        };
      }),
    }));
  } catch (error) {
    console.error(`Failed to get IFC properties of ${objectId}`, error);
    return [];
  }
}
